import {
  column,
  GridColumn,
  GridSortConfig,
  GridTableLayout,
  numericColumn,
  simpleDataRows,
  simpleHeader,
  SimpleHeaderAndData,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { percentageCell, priceCell } from "src/components";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { CostConfigurationSummaryOrderBy, Order } from "src/generated/graphqlTypes";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { formatNumberToString } from "src/utils";
import { parseOrder, toOrder } from "src/utils/ordering";
import {
  AllConfigurations_CostConfigurationSummaryFragment,
  AllConfigurations_PotentialBidPlanDimensionResultFragment,
  AllConfigurationsQuery,
  useAllConfigurationsDimensionsQuery,
  useAllConfigurationsQuery,
} from "./AllConfigurationsTab.generated";
import {
  ALL_CONFIGURATIONS_PAGE_SIZE,
  AllConfigurationsFilter,
  createFilterDefs,
  mapToFilter,
  typeDimensions,
} from "./utils";

type AllConfigurationsTabProps = { productOfferings: NamedFragment[] };

export function AllConfigurationsTab({ productOfferings }: AllConfigurationsTabProps) {
  const { id: projectId } = useProjectContext();
  const productOfferingIds = useMemo(() => productOfferings.map((po) => po.id), [productOfferings]);
  const dimensionsQuery = useAllConfigurationsDimensionsQuery({
    variables: { productOfferingIds },
    skip: productOfferingIds.isEmpty,
  });
  const dimensions = useMemo(() => dimensionsQuery.data?.potentialBidPlanDimensions ?? [], [dimensionsQuery.data]);
  const filterDefs = useMemo(() => createFilterDefs(productOfferings, dimensions), [productOfferings, dimensions]);
  const columns = useMemo(() => createColumns(dimensions), [dimensions]);
  const layoutState = useGridTableLayoutState<AllConfigurationsFilter>({
    persistedFilter: { filterDefs, storageKey: `${projectId}_costAnalysisAllConfigurationsFilter` },
    search: "server",
  });
  const [orderBy, setOrderBy] = useState<CostConfigurationSummaryOrderBy>(defaultAllConfigurationsOrderBy);
  const query = useAllConfigurationsQuery({
    variables: {
      filter: mapToFilter(layoutState.filter, layoutState.searchString, productOfferingIds),
      orderBy,
      page: { offset: 0, limit: ALL_CONFIGURATIONS_PAGE_SIZE },
    },
    skip: productOfferingIds.isEmpty,
  });
  const sorting: GridSortConfig = useMemo(
    () => ({
      on: "server",
      onSort: (key, direction) => setOrderBy(toOrder(key, direction)),
      value: parseOrder(orderBy),
    }),
    [orderBy],
  );
  const createRows = useCallback(
    (data: AllConfigurationsQuery | undefined) => [
      simpleHeader,
      ...simpleDataRows(data?.costConfigurationSummaries.entities),
    ],
    [],
  );
  const maybeFetchNextPage = useCallback(async () => {
    const page = query.data?.costConfigurationSummaries;
    if (page?.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: page.entities.length, limit: ALL_CONFIGURATIONS_PAGE_SIZE } },
      });
    }
  }, [query]);
  return (
    <GridTableLayout
      layoutState={layoutState}
      emptyFallback="There aren't any configurations to display."
      tableProps={{
        query,
        createRows,
        columns,
        sorting,
        as: "virtual",
        infiniteScroll: { onEndReached: maybeFetchNextPage },
      }}
    />
  );
}

export const defaultAllConfigurationsOrderBy: CostConfigurationSummaryOrderBy = { productOffering: Order.Asc };

type Row = SimpleHeaderAndData<AllConfigurations_CostConfigurationSummaryFragment>;

function createColumns(dimensions: AllConfigurations_PotentialBidPlanDimensionResultFragment[]): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "productOffering",
      name: "Product Offering",
      header: "Product Offering",
      serverSideSortKey: "productOffering",
      canHide: false,
      data: ({ productOffering }) => productOffering.name,
      mw: "200px",
    }),
    ...typeDimensions(dimensions).map(({ type, displayName }) =>
      column<Row>({
        id: type.id,
        name: displayName,
        header: displayName,
        canHide: false,
        data: ({ pinnedOptions }) => pinnedOptions.find((rpo) => rpo.type.id === type.id)?.name,
      }),
    ),
    numericColumn<Row>({
      id: "totalCost",
      name: "Total Cost",
      header: "Total Cost",
      serverSideSortKey: "totalCostInCents",
      canHide: true,
      data: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
    }),
    numericColumn<Row>({
      id: "costPerSellableSqft",
      name: "Cost / Sq Ft",
      header: "Cost / Sq Ft",
      serverSideSortKey: "costPerSellableSqftInCents",
      canHide: true,
      data: ({ costPerSellableSqftInCents }) => priceCell({ valueInCents: costPerSellableSqftInCents }),
    }),
    numericColumn<Row>({
      id: "sellableSqft",
      name: "Sq. Ft.",
      header: "Sq. Ft.",
      serverSideSortKey: "sellableSqft",
      canHide: true,
      initHidden: true,
      data: ({ programData }) =>
        programData.sellableSqft ? formatNumberToString(programData.sellableSqft, true) : "-",
    }),
    numericColumn<Row>({
      id: "totalPrice",
      name: "Total Price",
      header: "Total Price",
      serverSideSortKey: "totalPriceInCents",
      canHide: true,
      initHidden: true,
      data: ({ totalPriceInCents }) => priceCell({ valueInCents: totalPriceInCents }),
    }),
    numericColumn<Row>({
      id: "indirectCostInCents",
      name: "Indirect Hard Costs",
      header: "Indirect Hard Costs",
      serverSideSortKey: "indirectCostInCents",
      canHide: true,
      initHidden: true,
      data: ({ indirectCostInCents }) => priceCell({ valueInCents: indirectCostInCents }),
    }),
    numericColumn<Row>({
      id: "directCostInCents",
      name: "Direct Hard Costs",
      header: "Direct Hard Costs",
      serverSideSortKey: "directCostInCents",
      canHide: true,
      initHidden: true,
      data: ({ directCostInCents }) => priceCell({ valueInCents: directCostInCents }),
    }),
    numericColumn<Row>({
      id: "provisionalCostsInCents",
      name: "Provisional Estimates $",
      header: "Provisional Estimates $",
      serverSideSortKey: "provisionalCostsInCents",
      canHide: true,
      initHidden: true,
      data: ({ provisionalCostsInCents }) => priceCell({ valueInCents: provisionalCostsInCents }),
    }),
    numericColumn<Row>({
      id: "provisionalPercentage",
      name: "Provisional Estimates %",
      header: "Provisional Estimates %",
      serverSideSortKey: "provisionalPercentage",
      canHide: true,
      initHidden: true,
      data: ({ provisionalPercentage }) => percentageCell(provisionalPercentage),
    }),
    numericColumn<Row>({
      id: "softCostInCents",
      name: "Soft Costs",
      header: "Soft Costs",
      serverSideSortKey: "softCostInCents",
      canHide: true,
      initHidden: true,
      data: ({ softCostInCents }) => priceCell({ valueInCents: softCostInCents }),
    }),
    numericColumn<Row>({
      id: "contingencyMarkupPercentage",
      name: "Contingency %",
      header: "Contingency %",
      serverSideSortKey: "contingencyMarkupPercentage",
      canHide: true,
      initHidden: true,
      data: ({ productOffering }) => percentageCell(productOffering.contingencyMarkupPercentage),
    }),
    numericColumn<Row>({
      id: "contingencyInCents",
      name: "Contingency $",
      header: "Contingency $",
      serverSideSortKey: "contingencyInCents",
      canHide: true,
      initHidden: true,
      data: ({ contingencyInCents }) => priceCell({ valueInCents: contingencyInCents }),
    }),
  ];
}
